import { motion } from "framer-motion";
import { ShieldCheck, Download, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import policyLines from "@/data/privacyPolicy.json";

const EASE = [0.22, 1, 0.36, 1];

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
    ) : (
      p
    )
  );
}

function DocLine({ text }) {
  if (text.startsWith("### ")) {
    return (
      <h3 className="mt-6 font-display text-base font-bold text-slate-800 sm:text-lg">
        {renderInline(text.slice(4))}
      </h3>
    );
  }
  if (text.startsWith("## ")) {
    return (
      <h2 className="mt-10 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
        {renderInline(text.slice(3))}
      </h2>
    );
  }
  if (text === "---") return <hr className="my-8 border-slate-200" />;
  if (text.startsWith("- ")) {
    return (
      <li className="ml-5 list-disc text-sm leading-relaxed text-slate-700 sm:text-base">
        {renderInline(text.slice(2))}
      </li>
    );
  }
  return <p className="text-sm leading-relaxed text-slate-700 sm:text-base">{renderInline(text)}</p>;
}

export default function PrivacyPage() {
  const lines = policyLines.filter((l) => !l.startsWith("# Privacy Policy"));

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 pb-28 pt-32 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700"
        >
          <ShieldCheck size={13} /> Legal
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
          className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl"
        >
          Privacy Policy
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-5 flex flex-wrap items-center gap-4"
        >
          <p className="max-w-xl text-base leading-relaxed text-slate-600">
            How Jyoti Academy collects, uses and protects your information.
          </p>
          <a
            href="/assets/privacy-policy.docx"
            download
            data-testid="privacy-download-docx"
            className="group inline-flex items-center gap-2 rounded-full border-2 border-sky-700 px-5 py-2 text-xs font-bold text-sky-700 transition-colors duration-200 hover:bg-sky-700 hover:text-white"
          >
            <Download size={14} /> Download DOCX
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: EASE }}
          className="mt-10 space-y-3 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10"
          data-testid="privacy-content"
        >
          {lines.map((l, i) => (
            <DocLine key={i} text={l} />
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-10 text-center text-xs text-slate-500"
        >
          Privacy questions? WhatsApp 77519 46736 (message only) or email hi.jyotiacademy@gmail.com
        </motion.p>
      </main>
      <Footer />
    </div>
  );
}
